import queryString       from 'query-string'
import * as R            from 'ramda'
import { connectRoutes } from 'redux-first-router'
import * as Gtag         from '../gtag'

// ==================================================================================================================
//
// Constants
//
// ==================================================================================================================

const ROUTE_TYPES = {
    POI               : 'POI_ROUTE',
    VENUE             : 'VENUE_ROUTE',
    VENUES            : 'VENUES_ROUTE',
    LOGIN             : 'LOGIN_ROUTE',
    DASHBOARD         : 'DASHBOARD_ROUTE',
    PLAYGROUND        : 'PLAYGROUND_ROUTE',
    MAPNOTE           : 'MAPNOTE_ROUTE',
    COMMAND_LINE_TOOLS: 'COMMAND_LINE_TOOLS_ROUTE',
    IMPORT_EXPORT     : 'IMPORT_EXPORT_ROUTE',
    ERROR_PAGE        : 'ERROR_PAGE_ROUTE',
    MOD_WIZARD        : 'MOD_WIZARD_ROUTE',
    VISITOR_INSIGHTS  : 'VISITOR_INSIGHTS_ROUTE',
}

/*
 * Hacks to avoid moving the wrong code into this file:
 *
 * react-first-router allows you to specify behavior in many circumstances including before and after the route
 * is about to change and when the location is changing to a specific location
 *
 * We used to keep all this behavior in this file, but then this file knew about all kinds of things
 * unrelated to routing.
 *
 * To avoid that, finalizeRouting must be called (by other files) to inject code to be run when certain routing
 * actions happen. That way, routing is just about routing. These functions can (must) be injected:
 *
 * - venueThunk        : dispatched when the location has changed to '/' or '/venues'
 * - venuesThunk       : dispatched when the location has changed to '/venue/...'
 * - beforeRouteChange : the location is about to change; do whatever you'd like in anticipation
 * - afterRouteChange  : the location just changed; do whatever you'd like
 *
 * This last one is initialized internally and need not be injected:
 *
 * - initialDispatch   : the very first dispatch will call this function
 */

// the injected functions to call as described above
const finalizedOps = {
    initialDispatch  : () => console.error('Initial dispatch called before router was initialized'),
    venuesThunk      : () => console.error('venuesThunk was called before finalizeRouter was called'),
    venueThunk       : () => console.error('venueThunk was called before finalizeRouter was called'),
    cmdThunk         : () => console.error('cmdThunk was called before finalizeRouter was called'),
    modWizardThunk   : () => console.error('modWizardThunk was called before finalizeRouter was called'),
    beforeRouteChange: () => console.error('called beforeRouteChange before finalizeRouting was called'),
    afterRouteChange : () => console.error('called afterRouteChange before finalizeRouting was called'),
}

/*
 * You must call this function before the first route in order to inject the real functions to be used
 */
const finalizeRouting = options => {
    finalizedOps.beforeRouteChange = options.beforeRouteChange
    finalizedOps.afterRouteChange  = options.afterRouteChange
    finalizedOps.venueThunk        = options.venueThunk
    finalizedOps.venuesThunk       = options.venuesThunk
    finalizedOps.cmdThunk          = options.cmdThunk
    finalizedOps.modWizardThunk    = options.modWizardThunk
    finalizedOps.visitorInsightsThunk = options.visitorInsightsThunk
}

// ==================================================================================================================
//
// Initialize routing (for redux-first-router)
//
// ==================================================================================================================
/*
 * BEFORE we change routes: Check for unsaved changes to a POI (and show a dialog if needed)
 * Dispatches
 * @sig beforeRouteChange :: (DispatchFunction, GetStateFunction, { action: Location }) -> ()
 */
const beforeRouteChange = (dispatch, getState, bag) => finalizedOps.beforeRouteChange(dispatch, getState, bag)

/*
 * AFTER we change routes: if we've landed on a POI and we weren't on that POI before, zoom to it on the map
 * Dispatches
 * @sig afterRouteChange :: () => (DispatchFunction, GetStateFunction) -> ()
 */
const afterRouteChange = (dispatch, getState, bag) => finalizedOps.afterRouteChange(dispatch, getState, bag)

/*
 * Dispatched when the location has changed to '/' or '/venues'
 */
const venuesThunk = (dispatch, getState) => finalizedOps.venuesThunk(dispatch, getState)

/*
 * Dispatched when the location has changed to '/venue/...'
 */
const venueThunk  = (dispatch, getState) => finalizedOps.venueThunk(dispatch, getState)

const cmdThunk = (dispatch, getState) => finalizedOps.cmdThunk(dispatch, getState)

const modWizardThunk = (dispatch, getState) => finalizedOps.modWizardThunk(dispatch, getState)

const visitorInsightsThunk = (dispatch, getState) => finalizedOps.visitorInsightsThunk(dispatch, getState)

const routesMap = ({
    [ROUTE_TYPES.DASHBOARD]          : { path: '/',                               thunk: venuesThunk },
    [ROUTE_TYPES.LOGIN]              : { path: '/login'                                              },
    [ROUTE_TYPES.PLAYGROUND]         : { path: '/playground'                                         },
    [ROUTE_TYPES.POI]                : { path: '/venues/:venueId/poi/:poiId',      thunk: venueThunk },
    [ROUTE_TYPES.VENUES]             : { path: '/venues',                         thunk: venuesThunk },
    [ROUTE_TYPES.VENUE]              : { path: '/venues/:venueId',                 thunk: venueThunk },
    [ROUTE_TYPES.MAPNOTE]            : { path: '/venues/:venueId/mapNote/:noteId', thunk: venueThunk },
    [ROUTE_TYPES.COMMAND_LINE_TOOLS] : { path: '/commandLineTools',                  thunk: cmdThunk },
    [ROUTE_TYPES.IMPORT_EXPORT]      : { path: '/importExport'                                       },
    [ROUTE_TYPES.ERROR_PAGE]         : { path: '/error'                                              },
    [ROUTE_TYPES.MOD_WIZARD]         : { path: '/ondisplay',                   thunk: modWizardThunk },
    [ROUTE_TYPES.VISITOR_INSIGHTS]   : { path: '/visitorinsights',       thunk: visitorInsightsThunk },
})

// initialize redux-first-router
// @sig initializeRouting :: History -> { reducer:, enhancer:, middleware: }
// @see make-vms-store
const initializeRouting = history => {

    const routingObject = connectRoutes(history, routesMap, {
        querySerializer: queryString,
        onBeforeChange : beforeRouteChange,
        onAfterChange  : afterRouteChange,
        initialDispatch: false,
    })

    finalizedOps.initialDispatch = routingObject.initialDispatch

    return routingObject
}

// ==================================================================================================================
//
// Action Creators
//
// ==================================================================================================================

const Actions = {
    changeTab       : (l, newTab)                 => ({ type: l.type, payload: l.payload, query: { ...l.query, tab: newTab } }),
    changeView      : (l, newView)                => handleChangeView(l, newView),
    goToInitialRoute: ()                          => finalizedOps.initialDispatch(),

    goToDashboard   : ()                            => ({ type: ROUTE_TYPES.DASHBOARD }),
    goToLogin       : ()                            => ({ type: ROUTE_TYPES.LOGIN }),
    goToMapNote     : (venueId, noteId, query = {}) => handleGoToMapNote(venueId, noteId, query),
    goToPlayground  : ()                            => ({ type: ROUTE_TYPES.PLAYGROUND }),
    goToPoi         : (venueId, poiId, query = {})  => handleGoToPoi(venueId, poiId, query),
    goToVenue       : (venueId, query = {})         => handleGoToVenue(venueId, query),
    goToImportExport: ()                            => ({ type: ROUTE_TYPES.IMPORT_EXPORT }),
    goToVenues      : ()                            => ({ type: ROUTE_TYPES.VENUES }),
    goToModWizard   : ()                            => ({ type: ROUTE_TYPES.MOD_WIZARD }),
    goToVisitorInsights   : ()                      => ({ type: ROUTE_TYPES.VISITOR_INSIGHTS }),
    goToErrorPage   : (error)                       => ({ type: ROUTE_TYPES.ERROR_PAGE, payload: { error } }),

    // develop only
    goToCommandLineTools: () => ({ type: ROUTE_TYPES.COMMAND_LINE_TOOLS }),
}

const handleChangeView = (l, newView) => {
    Gtag.recordViewChange(l, newView)

    return ({ type: l.type, payload: l.payload, query: { ...l.query, view: newView } })
}

const handleGoToPoi = (venueId, poiId, query = {}) => {
    Gtag.recordPoiClick(venueId, poiId, query)

    return ({ type: ROUTE_TYPES.POI, query, payload: { venueId, poiId } })
}

const handleGoToMapNote = (venueId, noteId, query = {}) => {
    Gtag.recordMapNoteClick(venueId, noteId)

    return ({ type: ROUTE_TYPES.MAPNOTE, query, payload: { venueId, noteId } })
}

const handleGoToVenue = (venueId, query = {}) => {
    Gtag.recordVenue(venueId, query)

    return { type: ROUTE_TYPES.VENUE, query, payload: { venueId } }
}

// ==================================================================================================================
//
// Selectors
//
// ==================================================================================================================

// master state
const getLocation                  = R.pathOr(null,   ['location'])
const getLocationType              = R.pathOr(null,   ['location', 'type'])
const getLocationLevelId           = R.pathOr(null,   ['location', 'query', 'levelId'])
const getLocationSelectedTabId     = R.pathOr('info', ['location', 'query', 'tab'])
const getLocationStructureId       = R.pathOr(null,   ['location', 'query', 'structureId'])
const getLocationView              = R.pathOr('list', ['location', 'query', 'view'])
const getLocationNoteId            = R.pathOr(null,   ['location', 'payload', 'noteId'])
const getLocationVenueId           = R.pathOr(null,   ['location', 'payload', 'venueId'])
const getLocationPoiId             = R.pathOr(null,   ['location', 'payload', 'poiId'])
const getLocationError             = R.pathOr(null,   ['location', 'payload', 'error'])
const getLocationPreviousMapNoteId = R.pathOr(null,   ['location', 'prev', 'payload', 'noteId'])
const getLocationPreviousPoiId     = R.pathOr(null,   ['location', 'prev', 'payload', 'poiId'])

// local state (slice for combineReducer)
const locLocationIsPoi         = R.propEq(ROUTE_TYPES.POI, 'type')
const locLocationPoiId         = R.pathOr(null,   ['payload', 'poiId'])
const locLocationVenueId       = R.pathOr(null,   ['payload', 'venueId'])
const locLocationView          = R.pathOr('list', ['query', 'view'])

/*
 * Return the location's new "poiId" -- but only if it has changed
 * @sig  locLocationPoiIdIfChanged :: Location -> undefined|PoiId
 */
const getLocationPoiIdIfChanged = masterState =>
    getLocationPreviousPoiId(masterState) === getLocationPoiId(masterState)
        ? undefined
        : getLocationPoiId(masterState)

/*
 * Return the location's new MapNoteId -- but only if it has changed
 * @sig  locLocationMapNoteIdIfChanged :: Location -> undefined|MapNoteId
 */
const getLocationMapNoteIdIfChanged = masterState =>
    getLocationPreviousMapNoteId(masterState) === getLocationNoteId(masterState)
        ?  undefined
        : getLocationNoteId(masterState)

/*
 * If we used to be on a POI route, are we no longer?
 * @sig  isNavigatingAwayFromEntity :: (Location, Location) -> Boolean
 */
const isNavigatingAwayFromEntity = (oldLocation, newLocation) =>
    locLocationIsPoi(oldLocation) && locLocationPoiId(oldLocation) !== locLocationPoiId(newLocation)

/*
 * Is the new location at a different venue?
 * @sig  isNavigatingToNewVenue :: (Location, Location) -> Boolean
 */
const isNavigatingToNewVenue = (oldLocation, newLocation) => {
    const oldVenueId = locLocationVenueId(oldLocation)
    const newVenueId = locLocationVenueId(newLocation)
    return oldVenueId && oldVenueId !== newVenueId
}

const Selectors       = {

    // master state
    getLocation,
    getLocationType,
    getLocationLevelId,
    getLocationSelectedTabId,
    getLocationStructureId,
    getLocationView,
    getLocationVenueId,
    getLocationPoiId,
    getLocationError,
    getLocationNoteId,
    getLocationPreviousMapNoteId,
    getLocationPoiIdIfChanged,
    getLocationMapNoteIdIfChanged,

    // local state (slice for combineReducer)
    locLocationIsPoi,
    locLocationPoiId,
    locLocationVenueId,
    locLocationView,

    isNavigatingAwayFromEntity,
    isNavigatingToNewVenue,
}

// ==================================================================================================================
//
// export
//
// ==================================================================================================================

export {
    ROUTE_TYPES,

    initializeRouting,
    finalizeRouting,

    Actions,
    Selectors,
}


